import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import './connections-premium.scss';
import './connections-dashboard.scss';
import {
  ContentLayout,
  Header,
  SpaceBetween,
  Container,
  Select,
  SelectProps,
  FormField,
  Grid,
  AppLayout,
  Spinner,
  Alert,
  Badge,
  Box,
  Link,
  Popover,
  HelpPanel
} from '@amzn/awsui-components-core';
import { LeaderScorecardGrid } from './LeaderScorecardGrid';

import { CrossTeamComparison } from './CrossTeamComparison';
import { FreetextTopicsTab } from './FreetextTopicsTab';
import { useConnectionsLeaders, useConnectionsScorecards, useConnectionsTrends, useViewerContext } from './useConnectionsData';
import { CONNECTIONS_THEMES, CONNECTIONS_SCOPES, CONNECTIONS_SUMMARY_TYPES, ConnectionsScorecard } from './types';
import { ScorecardHero } from './sections/ScorecardHero';
import { HighlightsLowlights } from './sections/HighlightsLowlights';
import { MultiThemeTrend } from './sections/MultiThemeTrend';
import { FreetextSignalsCard } from './sections/FreetextSignalsCard';
import { SentimentDeepDive } from './sections/SentimentDeepDive';
import { HeadcountInsights } from './sections/HeadcountInsights';
import { AIInsightsSummary } from './sections/AIInsightsSummary';
import { QuestionSentimentSummary } from './sections/QuestionSentimentSummary';
import { ExpectedRangeGap } from './sections/ExpectedRangeGap';
import { AvatarLeaderDropdown } from './AvatarLeaderDropdown';

/**
 * Format a period label for the selected cadence.
 * - MONTHLY: single calendar month, e.g. "2026-06-01" -> "Jun 2026".
 * - MONTHLY_TRAILING_THREE_MONTHS (T3M): startDate is the FIRST month; end month = startDate + 2 months.
 *     "2026-01-01" -> "Jan - Mar 2026" (same year)
 *     "2025-11-01" -> "Nov 2025 - Jan 2026" (cross-year)
 */
function formatPeriodLabel(startDate: string, metricPeriod: string): string {
  const start = new Date(startDate + 'T00:00:00');
  if (isNaN(start.getTime())) return startDate;
  if (metricPeriod === 'MONTHLY') {
    return start.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  }
  const end = new Date(start);
  end.setMonth(end.getMonth() + 2);
  const startMon = start.toLocaleDateString('en-US', { month: 'short' });
  const startYear = start.getFullYear();
  const endMon = end.toLocaleDateString('en-US', { month: 'short' });
  const endYear = end.getFullYear();
  if (startYear === endYear) {
    return `${startMon} - ${endMon} ${endYear}`;
  }
  return `${startMon} ${startYear} - ${endMon} ${endYear}`;
}

const SCOPE_OPTIONS: SelectProps.Option[] = CONNECTIONS_SCOPES.map((s) => ({
  value: s.value,
  label: s.label
}));

const DEFAULT_SUMMARY_TYPE = CONNECTIONS_SUMMARY_TYPES[0].value;

/**
 * Review cadences offered in the Cadence dropdown. Only cadences with org-wide data
 * coverage are listed: MONTHLY and MONTHLY_TRAILING_THREE_MONTHS. DAILY /
 * DAILY_TRAILING_SEVEN_DAYS are excluded because their small samples are almost
 * entirely privacy-bar suppressed upstream (1-8 leaders), so they cannot back an
 * org-wide view. QUARTERLY is not yet emitted by the source and needs upstream work.
 */
const CADENCE_OPTIONS: SelectProps.Option[] = [
  { value: 'MONTHLY_TRAILING_THREE_MONTHS', label: 'Trailing 3 months' },
  { value: 'MONTHLY', label: 'Monthly' }
];

const TAB_INFO: Record<string, { title: string; body: React.ReactNode }> = {
  questionSentiment: {
    title: 'Question Sentiment Summary',
    body: (
      <>
        <p>
          A compact roll-up of the selected leader&apos;s Connections question responses for the chosen period. The single 100% stacked bar and the
          three KPI tiles aggregate every question the leader&apos;s org answered into one Positive / Negative / Neutral split.
        </p>
        <p>Responses are bucketed by their 5-point scale value:</p>
        <p>
          <strong className="cd-range--above">Positive</strong> - top-2-box (Agree / Strongly Agree, Very / Extremely Satisfied).
          <br />
          <strong className="cd-range--below">Negative</strong> - bottom-2-box (Disagree / Strongly Disagree, Slightly / Not-at-all Satisfied).
          <br />
          <strong className="cd-range--within">Neutral</strong> - the middle option (Neither Agree nor Disagree / Moderately Satisfied).
        </p>
        <p>
          The split is <strong>volume-weighted</strong> - it sums the response counts across all of the leader&apos;s questions, so higher-response
          questions carry more weight. Only true sentiment (Likert) questions are included; free-text and multi-select questions are excluded.
        </p>
        <p>
          <strong>Needs attention</strong> lists the three questions with the highest negative %, so the most actionable items surface without
          scrolling the full list.
        </p>
        <p>
          <strong>Scope:</strong> the numbers reflect the selected leader&apos;s org (their responses are already rolled up at the leader level) - not
          a sum of individual direct reports&apos; separate scorecards.
        </p>
      </>
    )
  },
  overview: {
    title: 'Theme Trend Over Time',
    body: (
      <>
        <p>
          A year-over-year line chart for one Connections theme at a time (choose it from the theme selector above the chart). The X axis is calendar
          months (Jan&ndash;Dec) and the Y axis is the selected leader&apos;s adjusted positive response rate (% positive). Two lines are drawn - the
          current year (solid) and the previous year (dashed) - so seasonality and year-over-year movement read at a glance.
        </p>
        <p>
          <strong>Useful for:</strong> seeing whether a theme is improving or declining versus the same month last year, and spotting within-year
          seasonality. Switch the selector to compare each of the four themes - Career Growth, Culture, Inclusion, Job Satisfaction.
        </p>
        <p>
          <strong>For peer comparison:</strong> see the 📋 Org Health Scorecard (current period, with ▲/●/▼ vs expected range) or 🎯 Value vs Expected
          range (peer comparison with min/mean/max threshold lines).
        </p>
        <p>
          <strong>Filter note:</strong> the Period dropdown does not affect this chart - it always shows full history split by year. Uses{' '}
          <code>MONTHLY_TRAILING_THREE_MONTHS</code> points bucketed by their start month. The previous-year line appears only when at least two years
          of data exist for the leader.
        </p>
      </>
    )
  },
  scorecards: {
    title: 'Leader Scorecards',
    body: (
      <>
        <p>The selected leader&apos;s current scores for the selected period - one row per leader, one column per theme.</p>
        <p>
          <strong className="cd-range--above">▲</strong> above expected range
        </p>
        <p>
          <strong className="cd-range--within">●</strong> within range
        </p>
        <p>
          <strong className="cd-range--below">▼</strong> below expected range
        </p>
        <p>
          Cells flagged with <strong>*</strong> have a sample size below 20.
        </p>
        <p>
          <strong>Hover</strong> any score to see the exact value, expected range, mean, and sample size.
        </p>
      </>
    )
  },
  breakdown: {
    title: 'Theme Scores by Leader',
    body: (
      <>
        <p>
          One row per direct report of the selected leader, scored across the 4 Connections themes (Career Growth, Culture, Inclusion, Job
          Satisfaction).
        </p>
        <p>
          Each row has an <strong>expand arrow</strong> - click to drill into that direct&apos;s own directs (recursive, supports L4 -&gt; L5 -&gt;
          L6).
        </p>
        <p>
          <strong>Population</strong> = how many employees in the leader&apos;s org were eligible for the survey.
          <br />
          <strong>Response Rate</strong> = what percentage of those eligible employees actually responded.
        </p>
        <p>
          <strong>Theme cells</strong> show the Adjusted Positive Response Rate (Adjusted PRR) for each theme:
        </p>
        <p>
          <strong className="cd-range--above">▲</strong> above expected range &nbsp;|&nbsp;
          <strong className="cd-range--within">●</strong> within expected range &nbsp;|&nbsp;
          <strong className="cd-range--below">▼</strong> below expected range
        </p>
        <p>
          Cells flagged with <strong>*</strong> have a sample size below 20 (interpret directionally).
        </p>
        <p>
          <strong>Hover</strong> any theme cell to see the exact per-theme values: Score, Range, Expected mean, Sample, Population, and Response rate.
          The row-level Population and Response Rate columns show the maximum across all 4 themes for the leader (typically identical since
          Connections surveys all themes together).
        </p>
        <p>
          <strong>Useful for:</strong> identifying which directs are below the expected range, spotting whether a high parent score is lifted by a few
          strong teams or evenly distributed.
        </p>
      </>
    )
  },
  comparison: {
    title: 'Cross-Team Comparison',
    body: (
      <>
        <p>
          Bar charts comparing all of the selected leader&apos;s directs side-by-side, one chart per theme. The summary cards at the top show how many
          direct reports are below the expected range for each theme - hover the count to see their names.
        </p>
        <p>
          A grouped column chart comparing all of the selected leader&apos;s directs across the 4 themes. Each leader is a colored column within each
          theme group; the legend shows each leader&apos;s photo and name.
        </p>
        <p>The summary cards above show how many direct reports are below the expected range for each theme - hover a count to see their names.</p>
        <p>
          <strong>Useful for:</strong> visual rank ordering, identifying systemic issues (e.g., 5/7 directs below on Inclusion → org-wide problem, not
          individual).
        </p>
      </>
    )
  },
  sentimentDeepDive: {
    title: 'Sentiment Deep Dive',
    body: (
      <>
        <p>
          Two horizontal bar charts — negative and positive — where each bar is a &ldquo;Leader · Topic&rdquo; pair sized by response count, across
          the selected leader and their direct reports. Sorted by count, capped at the top 15.
        </p>
        <p>
          <strong>Hover</strong> a bar to see that leader &amp; topic&apos;s sample size, positive %, and negative %.
        </p>
        <p>
          <strong>Source:</strong> Clarity&apos;s <strong>aggregated</strong> topic-sentiment values for the selected period. These totals may differ
          from the <em>Freetext responses &amp; word cloud</em> panel, which reflects the raw monthly file upload.
        </p>
      </>
    )
  },
  freetext: {
    title: 'Free Text Responses & Word Cloud',
    body: (
      <>
        <p>
          Raw verbatim free-text responses for the selected leader and period, shown as a word cloud (terms sized by frequency, colored by sentiment)
          with a click-to-drill-down list. Filter by sentiment or topic, or click a word, to see the underlying responses.
        </p>
        <p>
          <strong>Source &amp; caveat:</strong> this panel is populated from a <strong>monthly manual file upload</strong> (the Clarity free-text
          export), so the number of responses/questions shown here may <strong>differ</strong> from the totals in{' '}
          <em>Sentiment Deep Dive - Negative / Positive Topics</em>. Those charts use Clarity&apos;s <strong>aggregated</strong> topic-sentiment
          values (a different question scope and time window), whereas this drill-down reflects the <strong>raw uploaded responses</strong>. The two
          are not expected to reconcile exactly.
        </p>
      </>
    )
  },
  highlights: {
    title: 'Highlights / Lowlights',
    body: (
      <>
        <p>
          Surfaces the top 5 leader-by-theme rows that are <strong>above</strong> the expected range (Highlights) and <strong>below</strong> the
          expected range (Lowlights), based on the selected leader&apos;s direct reports.
        </p>
        <p>
          <strong className="cd-range--above">✅ Highlights</strong> - sorted by metric value DESC. The top performers stand out first.
        </p>
        <p>
          <strong className="cd-range--below">⚠️ Lowlights</strong> - sorted by metric value ASC. The most extreme below-range rows come first.
        </p>
        <p>
          <strong>Useful for:</strong> identifying systemic patterns (e.g., 4/7 directs below on Inclusion = org-wide concern, not one team).
        </p>
      </>
    )
  }
};

export const ConnectionsDashboard: React.FC = () => {
  const [selectedPeriod, setSelectedPeriod] = useState<SelectProps.Option | null>(null);
  const [selectedCadence, setSelectedCadence] = useState<SelectProps.Option>(CADENCE_OPTIONS[0]);
  const [selectedScope, setSelectedScope] = useState<SelectProps.Option>(SCOPE_OPTIONS[0]);
  const [selectedLeaderLogin, setSelectedLeaderLogin] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('dashboard');

  // Ribbon Canary logo/name click SPA-navigates to /canary; on each such navigation
  // (location.key changes) return to the Dashboard tab and scroll to the top -- the
  // "back to the landing view" behavior, without a full page reload. scrollIntoView on
  // a top anchor works regardless of whether the scroll container is the window or the
  // Cloudscape AppLayout content wrapper (window.scrollTo alone misses the latter).
  const location = useLocation();
  const topRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    setActiveTab('dashboard');
    topRef.current?.scrollIntoView({ block: 'start' });
    window.scrollTo({ top: 0 });
  }, [location.key]);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [activeInfoTab, setActiveInfoTab] = useState<string>('overview');

  const tabLabelWithInfo = (tabId: string, name: string) => {
    const handleClick = (e: React.SyntheticEvent) => {
      e.stopPropagation();
      // Toggle: if panel is open AND showing this tab's info, close it. Otherwise open with this tab's info.
      if (toolsOpen && activeInfoTab === tabId) {
        setToolsOpen(false);
      } else {
        setActiveInfoTab(tabId);
        setToolsOpen(true);
      }
    };
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
        {name}
        <span
          role="button"
          tabIndex={0}
          aria-label={`About the ${name} tab`}
          onClick={handleClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleClick(e);
            }
          }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '16px',
            height: '16px',
            borderRadius: '50%',
            border: '1px solid currentColor',
            fontSize: '11px',
            fontWeight: 600,
            fontStyle: 'italic',
            fontFamily: 'serif',
            cursor: 'pointer',
            opacity: 0.7,
            lineHeight: 1
          }}
        >
          i
        </span>
      </span>
    );
  };

  /**
   * Returns a Cloudscape Header.info Link that opens the side help panel
   * for the given section ID. Use as <Header info={sectionInfoLink('hero')}>.
   */
  const sectionInfoLink = (sectionId: string) => (
    <Link
      variant="info"
      onFollow={(e) => {
        e.preventDefault();
        if (toolsOpen && activeInfoTab === sectionId) {
          setToolsOpen(false);
        } else {
          setActiveInfoTab(sectionId);
          setToolsOpen(true);
        }
      }}
    >
      Info
    </Link>
  );

  // Fetch leader catalog
  const leadersState = useConnectionsLeaders(false);
  const { isPoweruser } = useViewerContext();

  // Sort leaders by org depth (level ascending) then name: root -> direct reports -> deeper chain.
  // Data-driven via the `level` field (replaces the old hardcoded TOBIAS_DIRECTS list, which also
  // omitted grettb). Auto-adapts: poweruser root = tobias (lowest level); scoped viewer root = self.
  const sortedLeaders = useMemo(() => {
    const all = leadersState.data || [];
    // Parse the integer out of level ("L4" / "4" / "Level 4"); null/unparseable sort last.
    const rank = (lvl: string | null) => {
      const m = (lvl || '').match(/\d+/);
      return m ? parseInt(m[0], 10) : Number.MAX_SAFE_INTEGER;
    };
    return [...all].sort((a, b) => {
      const d = rank(a.level) - rank(b.level);
      if (d !== 0) return d;
      return (a.displayName || a.leaderLogin).localeCompare(b.displayName || b.leaderLogin);
    });
  }, [leadersState.data]);

  // Default to first sorted leader (tobias) once loaded
  const effectiveLeaderLogin = selectedLeaderLogin || sortedLeaders[0]?.leaderLogin || null;

  // Fetch trends for first theme to derive available periods
  const periodsProbeState = useConnectionsTrends(effectiveLeaderLogin, CONNECTIONS_THEMES[0], '', DEFAULT_SUMMARY_TYPE);

  // Selected review cadence -> the metricPeriod value threaded into the scorecard queries below.
  const metricPeriod = (selectedCadence.value as string) || 'MONTHLY_TRAILING_THREE_MONTHS';

  const periodOptions: SelectProps.Option[] = useMemo(() => {
    if (!periodsProbeState.data || periodsProbeState.data.length === 0) return [];
    const periods = [...new Set(periodsProbeState.data.filter((d) => d.metricPeriod === metricPeriod).map((d) => d.startDate))].filter(Boolean);
    return periods
      .sort()
      .reverse()
      .map((p) => ({ value: p, label: formatPeriodLabel(p, metricPeriod) }));
  }, [periodsProbeState.data, metricPeriod]);

  // Auto-select most recent period once data arrives
  const effectivePeriod = selectedPeriod || periodOptions[0] || null;

  const leaderOptions: SelectProps.Option[] = useMemo(
    () =>
      sortedLeaders.map((l) => ({
        value: l.leaderLogin,
        label: l.displayName || l.leaderLogin,
        description: `${l.leaderLogin}@`
      })),
    [sortedLeaders]
  );

  const selectedLeader = useMemo(
    () => (leadersState.data || []).find((l) => l.leaderLogin === effectiveLeaderLogin),
    [leadersState.data, effectiveLeaderLogin]
  );

  // Context string shown in each section header so user knows which leader+period is active while scrolling
  const sectionContext = `${selectedLeader?.displayName || effectiveLeaderLogin || ''} - ${effectivePeriod?.label || ''}`;

  // Per-leader scorecard for the selected period (LEADER_ID dim — org-rolled-up)
  const leaderScorecardsState = useConnectionsScorecards(
    metricPeriod,
    'LEADER_ID',
    '',
    DEFAULT_SUMMARY_TYPE,
    effectiveLeaderLogin,
    effectivePeriod?.value || null
  );

  // Direct-org breakdown — only fires if leader supports drill-down
  const drillDownState = useConnectionsScorecards(
    selectedLeader?.hasDirectOrgBreakdown ? metricPeriod : null,
    'LEADER_ID:DIRECT_ORGS',
    '',
    DEFAULT_SUMMARY_TYPE,
    effectiveLeaderLogin,
    effectivePeriod?.value || null
  );

  // Selected leader + their directs — fed to the Sentiment Deep Dive fan-out
  // so each bar is a "<Leader> · <Topic>" pair.
  const freetextLeaders = useMemo(() => {
    const list: Array<{ login: string; name: string }> = [];
    if (effectiveLeaderLogin) {
      list.push({ login: effectiveLeaderLogin, name: selectedLeader?.displayName || effectiveLeaderLogin });
    }
    (drillDownState.data || []).forEach((sc) => {
      if (sc.leaderLogin && sc.leaderLogin !== effectiveLeaderLogin) {
        list.push({ login: sc.leaderLogin, name: sc.displayName || sc.leaderLogin });
      }
    });
    return list;
  }, [effectiveLeaderLogin, selectedLeader, drillDownState.data]);

  // Dashboard summary card -> full per-question breakdown in the Insights tab.
  return (
    <AppLayout
      navigationHide
      maxContentWidth={Number.MAX_VALUE}
      toolsOpen={toolsOpen}
      onToolsChange={({ detail }) => setToolsOpen(detail.open)}
      ariaLabels={{ toolsToggle: 'Canary info panel toggle' }}
      tools={<HelpPanel header={<h2>{TAB_INFO[activeInfoTab]?.title || 'Info'}</h2>}>{TAB_INFO[activeInfoTab]?.body || null}</HelpPanel>}
      content={
        <ContentLayout>
          <div className="connections-premium" ref={topRef}>
            <div className="cp-orbs" aria-hidden="true">
              <div className="cp-orb cp-orb-1" />
              <div className="cp-orb cp-orb-2" />
              <div className="cp-orb cp-orb-3" />
            </div>
            <SpaceBetween size="l">
              {/* Top-right cluster: About Connections + access badge (title/subtitle header removed; ribbon shows Canary) */}
              <div className="cp-topbar">
                <Link external href="https://ivy-help-center.talent.a2z.com/article/article-1573844430495-OtSe6cJaQ">
                  About Connections
                </Link>
                <Popover
                  triggerType="custom"
                  position="bottom"
                  size="medium"
                  header={isPoweruser ? 'Full access (poweruser)' : 'Scoped access (viewer)'}
                  content={
                    isPoweruser
                      ? 'You have full access to the Connections scorecard. You can view data for all leaders across the org. This tier is granted via the das-canary-scorecard-powerusers group.'
                      : 'Your access is scoped to your own organization. You can view scorecards only for yourself and everyone in your reporting chain; other leaders are hidden by design. Access is granted via the das-canary-scorecard-viewers group.'
                  }
                >
                  <span
                    style={{ cursor: 'pointer' }}
                    title={isPoweruser ? 'Full access - all leaders. Click for details.' : 'Scoped to your organization. Click for details.'}
                  >
                    <Badge color={isPoweruser ? 'blue' : 'grey'}>{isPoweruser ? 'Viewing: All Leaders' : 'Viewing: Your Organization'}</Badge>
                  </span>
                </Popover>
              </div>
              <Container>
                <Grid gridDefinition={[{ colspan: 3 }, { colspan: 3 }, { colspan: 3 }]}>
                  <FormField
                    label="Cadence"
                    constraintText={metricPeriod === 'MONTHLY' ? 'Monthly windows have smaller sample sizes than trailing 3 months' : undefined}
                  >
                    <Select
                      selectedOption={selectedCadence}
                      onChange={({ detail }) => {
                        setSelectedCadence(detail.selectedOption);
                        // Valid start-dates differ per cadence; reset so it re-defaults to the newest.
                        setSelectedPeriod(null);
                      }}
                      options={CADENCE_OPTIONS}
                      expandToViewport
                    />
                  </FormField>
                  <FormField label="Period">
                    <Select
                      selectedOption={effectivePeriod}
                      onChange={({ detail }) => setSelectedPeriod(detail.selectedOption)}
                      options={periodOptions}
                      placeholder={periodsProbeState.loading ? 'Loading...' : 'Select period'}
                      statusType={periodsProbeState.loading ? 'loading' : 'finished'}
                      expandToViewport
                    />
                  </FormField>
                  <FormField
                    label="Leader Focus"
                    constraintText={
                      selectedLeader && !selectedLeader.hasDirectOrgBreakdown
                        ? 'Drill-down unavailable - small team or insufficient sub-org data'
                        : undefined
                    }
                  >
                    <AvatarLeaderDropdown
                      options={leaderOptions.map((o) => ({ value: o.value || '', label: o.label || '', description: o.description }))}
                      selectedValue={effectiveLeaderLogin}
                      onChange={(val) => setSelectedLeaderLogin(val)}
                      loading={leadersState.loading}
                      placeholder="Select a leader"
                    />
                  </FormField>
                </Grid>
              </Container>

              {leadersState.error && (
                <Alert type="error" header="Failed to load leader list">
                  {leadersState.error.message}
                </Alert>
              )}

              {/* ===========================================================
                NARRATIVE LAYOUT — top-to-bottom story:
                1. ScorecardHero        — what is the headline for this leader?
                2. Team Breakdown       — leader x theme matrix (drill-down)
                3. Highlights/Lowlights — who is standing out, both directions?
                4. Value vs Expected    — visual rank ordering
                5. Theme Trend          — direction over time
                6. Freetext Signals     — top concerns from the team
            =========================================================== */}

              {/* Pill tabs (Item 3) */}
              <div className="cp-tabs">
                <button type="button" className={`cp-tab${activeTab === 'dashboard' ? ' active' : ''}`} onClick={() => setActiveTab('dashboard')}>
                  📊 Dashboard
                </button>
                <button type="button" className={`cp-tab${activeTab === 'insights' ? ' active' : ''}`} onClick={() => setActiveTab('insights')}>
                  💡 Insights
                </button>
              </div>

              {activeTab === 'dashboard' && (
                <SpaceBetween size="l">
                  {/* 1. Scorecard hero */}
                  <ScorecardHero
                    loading={leaderScorecardsState.loading}
                    error={leaderScorecardsState.error}
                    scorecard={(leaderScorecardsState.data && leaderScorecardsState.data[0]) || null}
                    leaderName={selectedLeader?.displayName || effectiveLeaderLogin || 'Leader'}
                    periodLabel={effectivePeriod?.label || ''}
                    infoLink={sectionInfoLink('scorecards')}
                  />

                  {/* 2. Team Breakdown (only when leader has drill-down data) */}
                  {selectedLeader?.hasDirectOrgBreakdown && (
                    <Container
                      header={
                        <Header
                          variant="h2"
                          description={`${selectedLeader.displayName || effectiveLeaderLogin}'s direct reports - click expand for sub-orgs`}
                          info={sectionInfoLink('breakdown')}
                        >
                          📊 Theme Scores by Leader
                        </Header>
                      }
                    >
                      <ScorecardsTabContent
                        loading={drillDownState.loading}
                        error={drillDownState.error}
                        scorecards={drillDownState.data || []}
                        enableDrillDown
                        startDate={effectivePeriod?.value || ''}
                        metricPeriod={metricPeriod}
                      />
                    </Container>
                  )}

                  {/* 4 + 5 in two-column grid (Item 6) */}
                  <div className="cp-grid-two">
                    <Container
                      header={
                        <Header
                          variant="h2"
                          actions={<span className="cd-section-context">{sectionContext}</span>}
                          description="Theme scores compared across the leader's directs"
                          info={sectionInfoLink('comparison')}
                        >
                          🎯 Value vs Expected Range
                        </Header>
                      }
                    >
                      {drillDownState.loading ? (
                        <Box textAlign="center" padding="xl">
                          <Spinner size="big" /> Loading cross-team data...
                        </Box>
                      ) : drillDownState.error ? (
                        <Alert type="error" header="Failed to load cross-team comparison">
                          {drillDownState.error.message}
                        </Alert>
                      ) : (
                        <CrossTeamComparison scorecards={drillDownState.data || []} period={effectivePeriod?.label || ''} />
                      )}
                    </Container>
                    <MultiThemeTrend
                      leaderLogin={effectiveLeaderLogin || ''}
                      scope={''}
                      summaryType={DEFAULT_SUMMARY_TYPE}
                      infoLink={sectionInfoLink('overview')}
                      sectionContext={sectionContext}
                    />
                  </div>

                  {/* 6. Heatmap + Gap in two-column grid (Items 4, 5, 6) */}
                  <div className="cp-grid-two">
                    <Container
                      header={
                        <Header
                          variant="h2"
                          actions={<span className="cd-section-context">{sectionContext}</span>}
                          info={sectionInfoLink('questionSentiment')}
                        >
                          🧭 Question Sentiment Summary
                        </Header>
                      }
                    >
                      <QuestionSentimentSummary
                        leaderLogin={effectiveLeaderLogin}
                        metricPeriod={metricPeriod}
                        startDate={effectivePeriod?.value || ''}
                        periodLabel={effectivePeriod?.label || ''}
                      />
                    </Container>
                    <Container
                      header={
                        <Header variant="h2" actions={<span className="cd-section-context">{sectionContext}</span>}>
                          📊 Expected-Range Gap
                        </Header>
                      }
                    >
                      <ExpectedRangeGap scorecards={drillDownState.data || []} periodLabel={effectivePeriod?.label || ''} />
                    </Container>
                  </div>

                  {/* Highlights / Lowlights (bottom of Dashboard) */}
                  <HighlightsLowlights
                    loading={drillDownState.loading}
                    error={drillDownState.error}
                    scorecards={drillDownState.data || []}
                    infoLink={sectionInfoLink('highlights')}
                    sectionContext={sectionContext}
                  />
                </SpaceBetween>
              )}

              {activeTab === 'insights' && (
                <SpaceBetween size="l">
                  {/* Read-with-care caveat, surfaced as lightweight info above the briefing */}
                  <Alert type="info" header="Read With Care">
                    Connection scores are adjusted positive-response rates measured against an ML-personalized expected range (team size, location,
                    job family, tenure) - not absolute truth. Two leaders with the same raw score can land in different above/within/below bands, and
                    small-sample reads (n ≤ 20) shown in the grid should be read directionally. Treat the dashboard as a prompt for a conversation,
                    not a verdict.
                  </Alert>

                  {/* AI-generated insights briefing - connection × headcount × attrition, playbook-grounded */}
                  <AIInsightsSummary
                    focusLeader={{
                      login: effectiveLeaderLogin || '',
                      name: selectedLeader?.displayName || effectiveLeaderLogin || 'Leader',
                      tier:
                        effectiveLeaderLogin === sortedLeaders[0]?.leaderLogin
                          ? 'ORG_LEADER'
                          : selectedLeader?.hasDirectOrgBreakdown
                          ? 'MGR_OF_MGRS'
                          : 'FRONT_LINE'
                    }}
                    leaders={freetextLeaders}
                    scorecards={drillDownState.data || []}
                    period={{ label: effectivePeriod?.label || '', startDate: effectivePeriod?.value || null, cadence: metricPeriod }}
                    leaderMatches={
                      selectedLeader?.hasDirectOrgBreakdown
                        ? // breakdown leader: the drill-down data must be stamped for THIS leader|period
                          drillDownState.key === `${effectiveLeaderLogin ?? ''}|${effectivePeriod?.value ?? ''}`
                        : // non-breakdown leader: fresh only once the drill-down has cleared (no stale directs)
                          drillDownState.data == null
                    }
                    dataLoading={drillDownState.loading}
                  />

                  {/* Headcount & attrition - connection ⋈ attrition retention narrative, directs-scoped (TM1 pipeline) */}
                  <HeadcountInsights leaders={freetextLeaders} scorecards={drillDownState.data || []} sectionContext={sectionContext} />

                  {/* Sentiment Deep Dive - negative / positive topic bars */}
                  <SentimentDeepDive
                    leaders={freetextLeaders}
                    metricPeriod={metricPeriod}
                    startDate={effectivePeriod?.value || ''}
                    periodLabel={effectivePeriod?.label || ''}
                    infoLink={sectionInfoLink('sentimentDeepDive')}
                  />

                  {/* Freetext responses - collapsible detail (word cloud + drill-down) */}
                  <Container
                    header={
                      <Header
                        variant="h2"
                        description="Raw verbatim responses for the selected leader and period - word cloud and drill-down"
                        info={sectionInfoLink('freetext')}
                      >
                        💬 Free Text Responses & Word Cloud
                      </Header>
                    }
                  >
                    <FreetextTopicsTab
                      leaderLogin={effectiveLeaderLogin || ''}
                      metricPeriod={metricPeriod}
                      startDate={effectivePeriod?.value || ''}
                      leaderName={selectedLeader?.displayName || effectiveLeaderLogin || ''}
                      periodLabel={effectivePeriod?.label || ''}
                    />
                  </Container>
                </SpaceBetween>
              )}

              {/* Footer */}
              <Box textAlign="center" color="text-body-secondary" padding={{ vertical: 'l' }}>
                <span style={{ fontSize: '11px' }}>Confidential · D&S Finance · Canary - Connection Intelligence for Leaders</span>
              </Box>
            </SpaceBetween>
          </div>
        </ContentLayout>
      }
    />
  );
};

// Subcomponent: scorecards table with loading state
interface ScorecardsTabContentProps {
  loading: boolean;
  error: Error | null;
  scorecards: ConnectionsScorecard[];
  enableDrillDown?: boolean;
  startDate?: string;
  metricPeriod?: string;
}

const ScorecardsTabContent: React.FC<ScorecardsTabContentProps> = ({ loading, error, scorecards, enableDrillDown, startDate, metricPeriod }) => {
  if (loading) {
    return (
      <Box textAlign="center" padding="xl">
        <Spinner size="big" /> Loading scorecards...
      </Box>
    );
  }
  if (error) {
    return (
      <Alert type="error" header="Failed to load scorecards">
        {error.message}
      </Alert>
    );
  }
  if (!scorecards || scorecards.length === 0) {
    return (
      <Alert type="info" header="No scorecard data">
        No scorecard data is available for the selected period and scope. Try a different period or scope.
      </Alert>
    );
  }
  return <LeaderScorecardGrid scorecards={scorecards} enableDrillDown={enableDrillDown} startDate={startDate} metricPeriod={metricPeriod} />;
};
